'use client';
import SubscriptionPlusEventApi from '@/api/events/subscription-plus';
import {
  SubsEventModalProvider,
  subsEventModalTypes,
  useSubsEventModal,
} from '@/app/(events)/events/subs_plus/index/components/SubsEventModal';
import MyProfileImg from '@/components/my/common/item/MyProfileImg';
import {getOS} from '@/utils';
import {
  handleLogin,
  handleUserInfo,
  inAppStore,
  loginState,
} from '@/utils/event/util';
import globalDomains from '@/utils/global-domains';
import {motion} from 'framer-motion';
import {useEffect, useMemo, useState} from 'react';
import styles from './styles.module.scss';

const EVENT_END_DATE = new Date('2025-06-18 23:59:59');

const SubscribePlus = ({onBodyId}) => {
  const [loading, setLoading] = useState(false);
  /** 이벤트 참여 여부 */
  const [isJoined, setIsJoined] = useState(false);
  /** 구독+ 구매 개수 */
  const [subscriptionCount, setSubscriptionCount] = useState(0);

  /** 로그인 한 사용자의 상태 */
  const {userId, userNickname} = useMemo(() => handleUserInfo(), []);
  /** 이벤트 기간 여부 */
  const isEventActive = useMemo(() => new Date() < EVENT_END_DATE, []);

  const {showAlert, showConfirm} = useSubsEventModal();

  useEffect(() => {
    /**
     * 초기 로드시 body id 셋팅
     */
    onBodyId && onBodyId(styles.body);
    setLoading(true);
  }, [onBodyId]);

  /** 화면 최초 렌더링 시 동작 */
  useEffect(() => {
    (async () => {
      if (!loginState) {
        setIsJoined(false);
      } else {
        try {
          const {result, data} =
            await SubscriptionPlusEventApi.checkUserHasJoined();

          if (result !== 1) {
            // Note. 참가 여부 확인 실패시에도 API 결과는 성공(200코드)으로 옴
            return;
          }

          const hasJoined = !!data;

          if (hasJoined) {
            const fetchedSubscriptionCount = await fetchSubscriptionCount();
            setSubscriptionCount(fetchedSubscriptionCount);
          }

          setIsJoined(hasJoined);
        } catch (error) {
          console.error(error);
        }
      }
    })();
  }, []);

  /**
   * 웹뷰로 접근했을 때 뒤로가기 동작 시 그냥 히스토리 상관 없이 바로 닫히도록 설정
   * Note. 이 코드는 웹뷰에서 포커스가 최소 1회 발동한 다음에만 정상 동작합니다.
   * 웹 브라우저의 설계상 정책으로 웹 페이지가 기기의 기본 뒤로가기 동작을 조작해 사용자의 뒤로가기 시 URL을 하이잭킹 하는 것을 막기 때문.
   * Ref. https://stackoverflow.com/questions/57339098/chrome-popstate-not-firing-on-back-button-if-no-user-interaction
   */
  useEffect(() => {
    window.history.replaceState({}, '', window.location.href);
    history.pushState(null, '', window.location.href);

    const handleOnPopState = () => {
      if (window.afreeca?.link?.isWebview()) {
        window.afreeca?.link?.closeWebview();
      }
    };

    window.addEventListener('popstate', handleOnPopState);

    return () => {
      window.removeEventListener('popstate', handleOnPopState);
    };
  }, []);

  /** 이벤트 기간 내 플러스 구독 이력 여부 확인 */
  const fetchSubscriptionCount = async () => {
    try {
      const {result: checkEventResult, data} =
        await SubscriptionPlusEventApi.checkEventSubscriptionPlusCount();
      const {errmsg, eventchkmsg} = data;

      if (checkEventResult !== 1) {
        console.log('구독 이력 확인에 실패했습니다.');
        console.log('errmsg:', errmsg);
      }

      const fetchedSubscriptionCount = Number(eventchkmsg) || subscriptionCount;

      return fetchedSubscriptionCount;
    } catch (error) {
      console.error(error);
    }
  };

  /** 구독하기 버튼 눌렀을 때 */
  const handleOnSubscription = () => {
    const isUnsupportedStore =
      window.afreeca.link.isWebview() && !['O', 'S'].includes(inAppStore());
    const userOS = getOS();

    if (isUnsupportedStore) {
      // 구매 불가 모달 출력 후 함수 탈출
      showAlert(() => {}, subsEventModalTypes.BUY_SUB_PLUS_ALERT);
      return;
    }

    let subsPlusPopup = null;
    if (window.afreeca.link.isMobile()) {
      // 모바일 구독 구매 페이지로 이동
      const popupUrl =
        userOS === 'mac'
          ? `${globalDomains.WWW_AFREECATV}/events/bridge?url=${encodeURIComponent(`${globalDomains.M_AFREECATV}/itemstore/a/subscription?tier_type=plus&entry_location=event_subs&szModel=web`)}`
          : `${globalDomains.M_AFREECATV}/itemstore/a/subscription?tier_type=plus&entry_location=event_subs&szModel=${window.afreeca.link.isWebview() ? 'app' : 'web'}`;

      subsPlusPopup = window.open(popupUrl, 'subscription_plus_popup');
    } else {
      const popupUrl = `${globalDomains.ITEM_AFREECATV}/pop_search_subscription.php?tierType=2&subscriptionType=auto&sys_type=web&location=event_subs&os=${userOS}`;

      subsPlusPopup = window.open(
        popupUrl,
        'subscription_plus_popup',
        'width=320,height=240,scrollbars=yes',
      );
    }

    // 팝업 닫힘 여부 확인
    const timer = setInterval(async () => {
      if (subsPlusPopup.closed) {
        clearInterval(timer);
        // 플러스 구독 이력 횟수 업데이트
        const fetchedSubscriptionCount = await fetchSubscriptionCount();
        setSubscriptionCount(fetchedSubscriptionCount);
      }
    });
  };

  /** 참여하기 버튼 눌렀을 때 */
  const handleOnJoin = async () => {
    try {
      if (!isEventActive) {
        showAlert(null, '', '이벤트 기간이 아닙니다.');
        return;
      }

      if (!loginState) {
        handleLogin();
        return;
      }

      // 이벤트 참여 API 호출
      const {result: joinEventResult, message} =
        await SubscriptionPlusEventApi.joinEvent();

      if (joinEventResult !== 1) {
        // 참여 API 동작에서 오류 발생 시 이 시퀀스 탈출
        showAlert(null, '', message);
        return;
      }

      setIsJoined(true);

      const fetchedSubscriptionCount = await fetchSubscriptionCount();
      setSubscriptionCount(fetchedSubscriptionCount);
      const hasSubscriptionPlus = fetchedSubscriptionCount > 0;

      /** 구매 불가능한 스토어 여부 확인 */
      const isUnsupportedStore =
        window.afreeca.link.isWebview() && !['O', 'S'].includes(inAppStore());

      if (!hasSubscriptionPlus) {
        // case 1. 이벤트 기간 내 플러스 구독 이력 없음
        if (isUnsupportedStore) {
          // case 1-1. 구매 불가
          showAlert(null, subsEventModalTypes.JOIN_NO_SUB_PLUS_ALERT);
        } else {
          // case 1-2. 구매 가능
          showConfirm(
            () => handleOnSubscription(),
            null,
            subsEventModalTypes.JOIN_NO_SUB_PLUS_CONFIRM,
          );
        }
      } else {
        // case 2. 이벤트 기간 내 플러스 구독 이력 있음
        if (isUnsupportedStore) {
          // case 2-1. 구매 불가
          showAlert(null, subsEventModalTypes.JOIN_YES_SUB_PLUS_ALERT);
        } else {
          // case 2-2. 구매 가능
          showConfirm(
            () => handleOnSubscription(),
            null,
            subsEventModalTypes.JOIN_YES_SUB_PLUS_CONFIRM,
          );
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    loading && (
      <div id={styles.wrap}>
        <header className={styles.header}>
          <div className={styles.inn}>
            <h1>
              <a
                href={`javascript:void(0)`}
                onClick={() => {
                  /**
                   * 웹뷰 -> home 이동 스킴이 없어 리로드로 적용
                   */
                  if (window.afreeca.link.isWebview()) {
                    window.location.reload();
                    return;
                  }

                  /**
                   * IOS 모웹에서 진입 시 예외처리
                   * 새 창에서 SOOP 모웹 메인으로 이동
                   */
                  if (window.afreeca.link.isMobile() && getOS() === 'mac') {
                    window.open(
                      `${globalDomains.WWW_AFREECATV}/events/bridge`,
                      '_blank',
                    );
                    return;
                  }

                  window.location.href = `${globalDomains.WWW_AFREECATV}`;
                  return;
                }}>
                SOOP
              </a>
            </h1>
            <div className={styles.login_wrap}>
              {loginState ? (
                <>
                  <div className={styles.user}>
                    <MyProfileImg userId={userId} className={styles.thumb} />
                    <span className={styles.nick}>{userNickname}</span>
                  </div>
                  <button
                    type='button'
                    className={styles.logout}
                    onClick={handleLogin}>
                    로그아웃
                  </button>
                </>
              ) : (
                <button
                  type='button'
                  className={styles.login}
                  onClick={handleLogin}>
                  로그인
                </button>
              )}
            </div>
          </div>
        </header>
        <section className={styles.visual}>
          <div className={styles.inn}>
            <div className={styles.sec_tit}>
              <h2>
                호구독은 없다 <strong>구독플러스</strong>등장 혜택폭격!
              </h2>
            </div>
            <p className={styles.desc}>
              신규 혜택과 <em>갤럭시 S25</em>까지 한번에!
            </p>
            <dl className={styles.period}>
              <dt>06/05~06/18</dt>
              <dd>06/26 당첨자 발표</dd>
            </dl>
          </div>
        </section>
        <section className={styles.sec01}>
          <div className={styles.inn}>
            <motion.h3
              className={styles.sec_tit}
              initial={{opacity: 0, y: 50}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: false}}
              transition={{ease: 'easeInOut', duration: 0.4}}>
              구독 플러스로 구독 시 자동응모
            </motion.h3>
            <p className={styles.noti}>
              정기구독권(<em>1</em>,<em>3</em>,<em>6</em>개월),자동결제(신규){' '}
              <strong>결제 시 응모</strong>됩니다.
            </p>
            <strong className={styles.desc}>
              구독 연장도 OK! 더 많이 구독 할수록 <strong>당첨 확률 UP!</strong>
            </strong>

            <ul className={styles.prize_list}>
              <li>
                <div className={styles.prize}>
                  <img
                    src='//res.sooplive.co.kr/eventzone/event/2025/subs_plus/img/galaxy_s25.png'
                    alt=''
                  />
                </div>
                <div className={styles.detail}>
                  <p className={styles.name}>갤럭시 S25 256GB</p>
                  <p className={styles.num}>(1명)</p>
                </div>
              </li>
              <li>
                <div className={styles.prize}>
                  <img
                    src='//res.sooplive.co.kr/eventzone/event/2025/subs_plus/img/galaxy_watch7.png'
                    alt=''
                  />
                </div>
                <div className={styles.detail}>
                  <p className={styles.name}>갤럭시 워치7 44mm GPS</p>
                  <p className={styles.num}>(4명)</p>
                </div>
              </li>
              <li>
                <div className={styles.prize}>
                  <img
                    src='//res.sooplive.co.kr/eventzone/event/2025/subs_plus/img/bbq.png'
                    alt=''
                  />
                </div>
                <div className={styles.detail}>
                  <p className={styles.name}>BBQ 황금올리브 + 콜라</p>
                  <p className={styles.num}>(100명)</p>
                </div>
              </li>
              <li>
                <div className={styles.prize}>
                  <img
                    src='//res.sooplive.co.kr/eventzone/event/2025/subs_plus/img/starbucks.png'
                    alt=''
                  />
                </div>
                <div className={styles.detail}>
                  <p className={styles.name}>스타벅스 아이스 아메리카노</p>
                  <p className={styles.num}>(300명)</p>
                </div>
              </li>
            </ul>

            <button
              type='button'
              className={styles.entry_enter}
              disabled={!isEventActive || isJoined}
              onClick={() => handleOnJoin()}>
              참여하기
            </button>
            <p className={styles.entry_noti}>
              ※ 선물권 결제, 기존 자동결제 배치 결제, 구독 플러스 자동결제로
              구독 연장/변경 신청, 구독 플러스 제공 혜택 중 &apos;최초 구독
              1개월 무료(자동결제)&apos;로 결제는 응모되지 않습니다.
            </p>

            {isJoined && (
              <>
                <div className={styles.entry_wrap}>
                  <span className={styles.entry_num}>
                    응모 횟수 : {subscriptionCount}
                  </span>
                  <button
                    type='button'
                    className={styles.plus_subs_enter}
                    disabled={!isEventActive}
                    onClick={() => handleOnSubscription()}>
                    플러스 구독하고 응모하기
                  </button>
                </div>
                <p className={styles.agree_noti}>
                  ※ 경품 발송을 위해 개인정보(휴대폰 번호, 주소) 업데이트 및
                  광고성 정보 수신 동의가 필요하며{' '}
                  <strong>미동의 시 당첨이 취소</strong> 될 수 있습니다.
                </p>
                <div className={styles.rt}>
                  <button
                    type='button'
                    role='link'
                    className={styles.personal_modi}
                    onClick={() => {
                      const url = window.afreeca.link.isMobile()
                        ? `${globalDomains.AUTH_M_AFREECATV}/myinfo/confirmPwd`
                        : `${globalDomains.MEMBER_AFREECATV}/app/user_info.php`;
                      window.open(url, '_blank');
                      return;
                    }}>
                    개인정보 수정
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
        <section className={styles.sec02}>
          <div className={styles.inn}>
            <motion.h3
              className={styles.sec_tit}
              initial={{opacity: 0, y: 50}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: false}}
              transition={{ease: 'easeInOut', duration: 0.4}}>
              새롭게 추가된 구독 플러스 혜택
            </motion.h3>
            <p className={styles.desc}>
              아래 혜택은<strong>스트리머가 직접 활성화</strong>하여 제공됩니다.
            </p>

            <ul className={styles.benefit_list}>
              <li className={styles.plus_liveVod}>
                <img
                  src='//res.sooplive.co.kr/eventzone/event/2025/subs_plus/img/plus_liveVod.png'
                  alt=''
                />
                <div className={styles.detail}>
                  <em>구독 플러스 전용</em>
                  <span>LIVE / 다시보기</span>
                </div>
              </li>
              <li className={styles.tts_free}>
                <img
                  src='//res.sooplive.co.kr/eventzone/event/2025/subs_plus/img/tts_free.png'
                  alt=''
                />
                <div className={styles.detail}>
                  <em>유료 전자비서 목소리</em>
                  <span>전체 무료 이용</span>
                </div>
              </li>
              <li className={styles.free_1m}>
                <img
                  src='//res.sooplive.co.kr/eventzone/event/2025/subs_plus/img/free_1m.png'
                  alt=''
                />
                <div className={styles.detail}>
                  <em>최초 구독 시 1개월 무료</em>
                  <span>(플러스 자동결제)</span>
                </div>
              </li>
              <li className={styles.timemachine}>
                <img
                  src='//res.sooplive.co.kr/eventzone/event/2025/subs_plus/img/timemachine.png'
                  alt=''
                />
                <div className={styles.detail}>
                  <em>LIVE 타임머신</em>
                  <span className={styles.coming_soon}>Comming soon</span>
                </div>
              </li>
            </ul>

            <button
              type='button'
              className={styles.benefit_detail}
              onClick={() => {
                window.open(
                  !window.afreeca.link.isMobile()
                    ? `${globalDomains.SOTONG_AFREECATV}/?board_type=servicenotice&work=view&b_no=8329`
                    : getOS() === 'mac'
                      ? `${globalDomains.WWW_AFREECATV}/events/bridge?url=${encodeURIComponent(`${globalDomains.M_AFREECATV}/notice/a/view/no/8329/platform/mobileweb`)}`
                      : `${globalDomains.M_AFREECATV}/notice/a/view/no/8329/platform/mobileweb`,

                  '_blank',
                );
              }}>
              혜택 자세히 보기
            </button>
          </div>
        </section>
        <section className={styles.sec03}>
          <div className={styles.inn}>
            <strong className={styles.noti_tit}>
              <em>이벤트</em> 유의사항
            </strong>
            <ul className={styles.noti_list}>
              <li>참여하기 버튼을 눌러야 이벤트에 참여됩니다.</li>
              <li>
                미션 완료 여부는 참여하기 버튼 누른 시점과 관계없이 이벤트 기간
                내에 결제한 이력을 확인합니다.(이벤트 기간 내 구독 결제 후
                참여하기 버튼 클릭 하여도 결제 인정)
              </li>
              <li>
                이벤트 기간은 2025년 6월 5일 00:00:00 부터 2025년 6월 18일
                23:59:59 로 해당 기간 내에 구독 플러스 1,3,6개월 정기 구독권 /
                자동결제(신규) 상품을 결제한 이력으로 자동 응모됩니다.
              </li>
              <li>
                구독 플러스 정기권으로 구독 연장 시에도 응모가 가능합니다.
              </li>
              <li>
                구독 플러스 선물권 결제, 기존 구독 플러스 자동결제의 배치 결제,
                구독 플러스 자동결제로 구독 연장/변경 신청, 구독 플러스 제공
                혜택 중 &apos;최초 구독 1개월 무료(자동결제)&apos;로 결제 시
                응모되지 않습니다.
              </li>
              <li>
                이벤트 당첨 전 구독 플러스 결제 취소 및 환불 등록 건은 응모
                횟수에서 차감됩니다.
              </li>
              <li>1계정 당 1개의 경품만 당첨됩니다.</li>
              <li>
                비정상적이거나 불법적인 방법으로 이벤트에 참여 하신 경우 당첨이
                취소될 수 있습니다.
              </li>
              <li>
                이벤트 당첨 결과는 2025년 6월 26일 &apos;알려드립니다&apos;
                게시판에 공지될 예정입니다.
              </li>
              <li>
                2025년 6월 26일에 치킨, 커피 교환권 당첨자분들께는 개인정보에
                등록하신 휴대폰 번호로 교환권 번호가 담긴 SMS가 발송되며, 갤럭시
                S25, 갤럭시 워치7 당첨자 분들은 경품 수령 주소 입력 링크가
                발송됩니다. 2025년 7월 1일까지 경품 수령 주소 입력하지 않을 시
                개인정보에 등록된 주소로 경품이 발송됩니다.
              </li>
              <li>
                갤럭시 S25, 갤럭시 워치7 당첨 시 제세공과금 22%는 본인 부담이며,
                제세공과급 미납 및 신고서 제출 미동의 시 당첨이 취소될 수
                있습니다.
              </li>
              <li>
                당첨된 경품은 양도/교환/재발송 등이 불가하며, 경품 반송 및
                분실은 당사에서 책임지지 않습니다.
              </li>
              <li>
                경품은 경품 판매처 사정에 의해 비슷한 금액의 다른 경품으로
                대체될 수 있으며, 대체 시 당첨자분들께 사전고지 됩니다.
              </li>
              <li>본 이벤트는 당사 사정에 의해 조기 종료될 수 있습니다.</li>
            </ul>
          </div>
        </section>
      </div>
    )
  );
};

const SubscribePlusRoot = () => {
  return (
    <SubsEventModalProvider>
      <SubscribePlus />
    </SubsEventModalProvider>
  );
};

export default SubscribePlusRoot;
